
import { defineComponent, reactive, toRefs, computed, nextTick, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { Close, RefreshRight, CircleClose, Back, Right } from '@element-plus/icons-vue'
import ScrollPane from './ScrollPane.vue'
import path from 'path'

interface TagView {
  fullPath: string
  path: string
  name: string
  title?: string
  meta?: any
  query?: any
}

export default defineComponent({
  components: { ScrollPane, Close, RefreshRight, CircleClose, Back, Right },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    
    const state = reactive({
      visible: false,
      top: 0,
      left: 0,
      selectedTag: {} as TagView,
      affixTags: [] as TagView[]
    })
    
    const visitedViews = computed(() => store.state.tagsView.visitedViews)
    const routes = computed(() => store.state.permission.routes)
    const theme = computed(() => store.state.settings.theme)
    
    watch(() => route.path, () => {
      addTags()
      moveToCurrentTag()
    })
    
    watch(() => state.visible, (value) => {
      if (value) {
        document.body.addEventListener('click', closeMenu)
      } else {
        document.body.removeEventListener('click', closeMenu)
      }
    })
    
    onMounted(() => {
      initTags()
      addTags()
    })
    
    const isActive = (r: TagView) => {
      return r.path === route.path
    }
    
    const activeStyle = (tag: TagView) => {
      if (!isActive(tag)) return {}
      return {
        "background-color": theme.value,
        "border-color": theme.value
      }
    }
    
    const isAffix = (tag: TagView) => {
      return tag.meta && tag.meta.affix
    }
    
    const isFirstView = () => {
      try {
        return state.selectedTag.fullPath === '/index' || state.selectedTag.fullPath === visitedViews.value[1].fullPath
      } catch (err) {
        return false
      }
    }
    
    const isLastView = () => {
      try {
        return state.selectedTag.fullPath === visitedViews.value[visitedViews.value.length - 1].fullPath
      } catch (err) {
        return false
      }
    }
    
    const filterAffixTags = (routes: any[], basePath = '/') => {
      let tags: TagView[] = []
      routes.forEach(route => {
        if (route.meta && route.meta.affix) {
          const tagPath = path.resolve(basePath, route.path)
          tags.push({
            fullPath: tagPath,
            path: tagPath,
            name: route.name,
            meta: { ...route.meta }
          })
        }
        if (route.children) {
          const tempTags = filterAffixTags(route.children, route.path)
          if (tempTags.length >= 1) {
            tags = [...tags, ...tempTags]
          }
        }
      })
      return tags
    }
    
    const initTags = () => {
      const affixTags = state.affixTags = filterAffixTags(routes.value)
      for (const tag of affixTags) {
        // 必须有tag name
        if (tag.name) {
          store.dispatch('tagsView/addVisitedView', tag)
        }
      }
    }
    
    const addTags = () => {
      const { name } = route
      if (name) {
        store.dispatch('tagsView/addView', route)
        if (route.meta.link) {
          store.dispatch('tagsView/addIframeView', route)
        }
      }
      return false
    }
    
    const moveToCurrentTag = () => {
      nextTick(() => {
        const tag = document.querySelector('.tags-view-item.active') as HTMLElement
        const scrollPaneRef = document.querySelector('.scroll-pane') as any
        if (tag && scrollPaneRef?.moveToTarget) {
          scrollPaneRef.moveToTarget(tag)
        }
      })
    }
    
    const refreshSelectedTag = (view: TagView) => {
      store.dispatch('tagsView/delCachedView', view).then(() => {
        const { fullPath } = view
        router.replace({
          path: '/redirect' + fullPath
        })
      })
      if (route.meta.link) {
        store.dispatch('tagsView/delIframeView', route)
      }
    }
    
    const closeSelectedTag = (view: TagView) => {
      store.dispatch('tagsView/delView', view).then(({ visitedViews }: { visitedViews: TagView[] }) => {
        if (isActive(view)) {
          toLastView(visitedViews, view)
        }
      })
    }
    
    const closeRightTags = () => {
      store.dispatch('tagsView/delRightTags', state.selectedTag).then((visitedViews: TagView[]) => {
        if (!visitedViews.find(i => i.fullPath === route.fullPath)) {
          toLastView(visitedViews)
        }
      })
    }
    
    const closeLeftTags = () => {
      store.dispatch('tagsView/delLeftTags', state.selectedTag).then((visitedViews: TagView[]) => {
        if (!visitedViews.find(i => i.fullPath === route.fullPath)) {
          toLastView(visitedViews)
        }
      })
    }
    
    const closeOthersTags = () => {
      router.push(state.selectedTag.fullPath).catch(() => {
        // 导航错误，有意忽略
      })
      store.dispatch('tagsView/delOthersViews', state.selectedTag).then(() => {
        moveToCurrentTag()
      })
    }
    
    const closeAllTags = (view: TagView) => {
      store.dispatch('tagsView/delAllViews').then(({ visitedViews }: { visitedViews: TagView[] }) => {
        if (state.affixTags.some(tag => tag.path === route.path)) {
          return
        }
        toLastView(visitedViews, view)
      })
    }
    
    const toLastView = (visitedViews: TagView[], view?: TagView) => {
      const latestView = visitedViews.slice(-1)[0]
      if (latestView) {
        router.push(latestView.fullPath)
      } else {
        // 现在默认是重定向到主页，如果没有标签视图
        // 您可以根据需要调整它
        if (view?.name === 'Dashboard') {
          // 重新加载主页
          router.replace({ path: '/redirect' + view.fullPath })
        } else {
          router.push('/')
        }
      }
    }
    
    const openMenu = (tag: TagView, e: MouseEvent) => {
      const menuMinWidth = 105
      const offsetLeft = (e.currentTarget as HTMLElement).getBoundingClientRect().left
      const offsetWidth = (e.currentTarget as HTMLElement).offsetWidth
      const maxLeft = offsetWidth - menuMinWidth
      const left = e.clientX - offsetLeft + 15
      
      if (left > maxLeft) {
        state.left = maxLeft
      } else {
        state.left = left
      }
      
      state.top = e.clientY
      state.visible = true
      state.selectedTag = tag
    }
    
    const closeMenu = () => {
      state.visible = false
    }
    
    const handleScroll = () => {
      closeMenu()
      // not empty, used for closing menu on scroll
    }
    
    const handleTagClick = (tag: TagView, navigate: () => void) => {
      // 如果点击的不是当前激活的标签，则进行路由跳转
      if (!isActive(tag)) {
        navigate()
      }
    }
    
    return {
      ...toRefs(state),
      visitedViews,
      isActive,
      activeStyle,
      isAffix,
      isFirstView,
      isLastView,
      filterAffixTags,
      initTags,
      addTags,
      moveToCurrentTag,
      refreshSelectedTag,
      closeSelectedTag,
      closeRightTags,
      closeLeftTags,
      closeOthersTags,
      closeAllTags,
      toLastView,
      openMenu,
      closeMenu,
      handleScroll,
      handleTagClick
    }
  }
})
